import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fmtError, BACKEND_BASE } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Camera, Upload, X, Loader2 } from "lucide-react";

const BACKEND = BACKEND_BASE;
const MAX_BYTES = 25 * 1024 * 1024;

export default function CreatePost() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [f, setF] = useState({ title: "", caption: "", media_url: "", media_type: "" });

  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const pickFromGallery = () => galleryRef.current?.click();
  const pickFromCamera = () => cameraRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking same file
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("File too large. Max 25MB.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    setProgress(0);
    try {
      const { data } = await api.post("/uploads/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const absolute = data.url.startsWith("http") ? data.url : `${BACKEND}${data.url}`;
      setF((p) => ({ ...p, media_url: absolute, media_type: data.media_type }));
      toast.success("Uploaded!");
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const clearMedia = () => setF((p) => ({ ...p, media_url: "", media_type: "" }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.media_url) { toast.error("Please add a photo or video"); return; }
    setBusy(true);
    try {
      await api.post("/posts", f);
      toast.success("Posted!");
      nav("/posts");
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const hasMedia = !!f.media_url;

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 pt-6 pb-32">
      <button onClick={() => nav(-1)} className="btn-nb mb-5" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" strokeWidth={2.5}/> {t("common.back")}
      </button>
      <h1 className="font-display font-black text-4xl sm:text-5xl mb-1 tracking-tight">
        {t("createPost.title")}
      </h1>
      <p className="text-[#4A4F58] mb-6">{t("createPost.subtitle")}</p>

      <form onSubmit={submit} className="card-nb p-6 bg-white space-y-4" data-testid="create-post-form">
        {/* Hidden inputs */}
        <input
          ref={galleryRef}
          type="file"
          accept="image/*,video/*"
          className="hidden"
          onChange={handleFile}
          data-testid="file-gallery-input"
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*,video/*"
          capture="environment"
          className="hidden"
          onChange={handleFile}
          data-testid="file-camera-input"
        />

        {!hasMedia && !uploading && (
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={pickFromGallery}
              className="btn-nb btn-nb-primary py-6 flex-col"
              data-testid="pick-gallery-btn"
            >
              <Upload className="w-6 h-6" strokeWidth={2.5}/>
              <span className="font-display font-bold">{t("post.gallery")}</span>
            </button>
            <button
              type="button"
              onClick={pickFromCamera}
              className="btn-nb btn-nb-info py-6 flex-col"
              data-testid="pick-camera-btn"
            >
              <Camera className="w-6 h-6" strokeWidth={2.5}/>
              <span className="font-display font-bold">{t("post.camera")}</span>
            </button>
          </div>
        )}

        {uploading && (
          <div className="rounded-2xl border border-[#0F1115]/10 bg-[#F4F2ED] p-6 text-center" data-testid="upload-progress">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#14C8B0]" strokeWidth={2.5}/>
            <p className="font-display font-bold mb-2">Uploading… {progress}%</p>
            <div className="w-full h-2 bg-[#E5E2D6] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#5EEAD4] to-[#14C8B0] transition-all" style={{ width: `${progress}%` }}/>
            </div>
          </div>
        )}

        {hasMedia && (
          <div className="relative rounded-2xl overflow-hidden border border-[#0F1115]/10 bg-[#F4F2ED]" data-testid="media-preview">
            {f.media_type === "video"
              ? <video src={f.media_url} controls className="w-full max-h-80"/>
              : <img src={f.media_url} alt="" className="w-full max-h-80 object-cover"/>}
            <button
              type="button"
              onClick={clearMedia}
              className="absolute top-2 right-2 w-9 h-9 rounded-full bg-[#0A0B0D]/80 text-white flex items-center justify-center hover:bg-[#0A0B0D]"
              data-testid="clear-media-btn"
              aria-label="Remove media"
            >
              <X className="w-5 h-5" strokeWidth={2.5}/>
            </button>
          </div>
        )}

        <div>
          <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("post.title")}</label>
          <input className="input-nb" maxLength={80} value={f.title} onChange={upd("title")} placeholder="Sunset run" data-testid="post-title"/>
        </div>

        <div>
          <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("post.caption")}</label>
          <textarea className="input-nb min-h-[100px]" maxLength={600} value={f.caption} onChange={upd("caption")} placeholder="Tell the story..." data-testid="post-caption"/>
        </div>

        <button type="submit" disabled={busy || uploading || !hasMedia} className="btn-nb btn-nb-primary w-full" data-testid="submit-post">
          {busy ? t("post.posting") : <><ImagePlus className="w-4 h-4" strokeWidth={2.5}/> {t("post.share")}</>}
        </button>
      </form>
    </div>
  );
}
